import React, { useState } from "react";
import { useWizardContext } from "../useWizardContext";
import { useToast } from "../../../../context/ToastContext";
import { MapPin, Calendar as CalendarIcon, Mic, HardHat, X } from "lucide-react";
import LocationSearch from "../../../common/LocationSearch";
import CustomCalendar from "../../../common/CustomCalendar";
import EventTypeSearch from "../../../common/EventTypeSearch";
import { MAX_EVENT_LOCATIONS } from "../../../../config/eventConfig";

const EventPlannerIcon = ({ className = "w-6 h-6 text-gray-900" }) => (
  <svg width="24" height="24" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="18" y2="6" />
    <line x1="3" y1="12" x2="15" y2="12" />
    <line x1="3" y1="18" x2="10" y2="18" />
    <line x1="16" y1="18" x2="22" y2="18" />
    <line x1="19" y1="15" x2="19" y2="21" />
  </svg>
);

export default function Step1_BasicInfo() {
  const { formData, updateField, nextStep, isDirectQuote, isEditMode } = useWizardContext();
  const { showError } = useToast();
  const [locationInputValue, setLocationInputValue] = useState("");

  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Minimum date for picking new dates is tomorrow. The calendar preserves the previously selected event date.
  const minDateForStart = tomorrow;

  const [showCalendar, setShowCalendar] = useState(false);
  
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = String(dateStr).split("T")[0].split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts.map(Number);
      if (y && m && d) return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  const dateStart = parseLocalDate(formData.startDate);
  const dateEnd = parseLocalDate(formData.endDate);
  const isMultiDay = formData.isMultiDay === true || formData.isMultiDay === 'true';

  const toLocalIsoDate = (date) => {
    if (!date) return "";
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split("T")[0];
  };

  const currentLocations = Array.isArray(formData.locations) ? formData.locations : [];

  const handleAddLocation = (city) => {
    if (!city) return;
    const cityName = typeof city === "object" ? city.name : city;
    const cityId = typeof city === "object" ? (city.id || city._id) : null;
    if (!cityName || !cityName.trim()) return;

    if (currentLocations.length >= MAX_EVENT_LOCATIONS) {
      showError(`Maximum ${MAX_EVENT_LOCATIONS} locations allowed.`);
      return;
    }

    // Prevent duplicates
    const exists = currentLocations.some((loc) => {
      const locName = typeof loc === "object" ? loc.name : loc;
      const locId = typeof loc === "object" ? (loc.id || loc._id) : null;
      if (cityId && locId && cityId.toString() === locId.toString()) return true;
      return locName && locName.toLowerCase().trim() === cityName.toLowerCase().trim();
    });

    if (exists) {
      showError(`"${cityName}" is already added.`);
      return;
    }

    const newLoc = typeof city === "object" ? city : { id: null, name: cityName.trim() };
    updateField({ locations: [...currentLocations, newLoc] });
  };

  const handleRemoveLocation = (indexToRemove) => {
    if (isEditMode) return;
    const updated = currentLocations.filter((_, idx) => idx !== indexToRemove);
    updateField({ locations: updated });
  };

  const handleNext = () => {
    if (!formData.eventName || !formData.eventName.trim()) {
      showError("Please enter an Event Name.");
      return;
    }
    if (!formData.eventType) {
      showError("Please select a Type of event.");
      return;
    }
    if (!currentLocations || currentLocations.length === 0) {
      showError("Please select at least one location.");
      return;
    }
    if (!formData.startDate) {
      showError("Please select a Start Date.");
      return;
    }
    const isMulti = formData.isMultiDay === true || formData.isMultiDay === 'true';

    if (isMulti && !formData.endDate) {
      showError("Please select an End Date for multi-day events.");
      return;
    }
    if (isMulti && formData.endDate && formData.endDate <= formData.startDate) {
      showError("End Date must be after Start Date.");
      return;
    }
    if (!isDirectQuote && !isEditMode && !formData.hiringRole) {
      showError("Please select who you are hiring.");
      return;
    }
    nextStep();
  };

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3 w-full animate-fadeIn flex-1 pt-1">
      {/* Section 1: Basics */}
      <div>
        <h3 className="text-base sm:text-lg font-bold text-gray-950 mb-1.5">Let's start with the basics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-2.5">
          {/* Event Name */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Event Name</label>
            <input
              type="text"
              placeholder="e.g Riya & Aman's Wedding"
              value={formData.eventName}
              onChange={(e) => updateField({ eventName: e.target.value })}
              className="w-full h-[36px] px-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
            />
          </div>

          {/* Type of Event */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Type of event</label>
            <EventTypeSearch
              value={formData.eventType}
              disabled={isEditMode}
              onChange={(item) => {
                updateField({ 
                  eventType: item.id, 
                  eventTypeModel: item.type,
                  eventCategoryData: item
                });
              }}
              placeholder="Wedding"
            />
          </div>

          {/* City Selection */}
          <div className="flex flex-col gap-0.5 sm:col-span-2">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Which city is your event in?</label>

            <LocationSearch
              name="location"
              label=""
              value={locationInputValue}
              clearOnSelect={true}
              disableCreate={true}
              includeInactive={true}
              onChange={(city) => {
                if (!isEditMode) {
                  handleAddLocation(city);
                  setLocationInputValue("");
                }
              }}
              placeholder="Select city"
              disabled={isEditMode}
            />

            {/* Chips Container */}
            {currentLocations.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-0.5">
                {currentLocations.map((loc, idx) => {
                  const nameStr = typeof loc === "object" && loc !== null ? loc.name : loc;
                  return (
                    <span
                      key={loc.id || loc._id || idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 text-[#FF4C18] border border-orange-200 rounded-full text-xs font-semibold"
                    >
                      <MapPin size={12} />
                      {nameStr}
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLocation(idx)}
                          className="hover:text-red-600 focus:outline-none ml-0.5"
                          title="Remove location"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Dates */}
      <div>
        <div className="flex items-center justify-between mb-1 gap-2 flex-wrap sm:flex-nowrap">
          <h3 className="text-base sm:text-lg font-bold text-gray-950 whitespace-nowrap">When is it happening?</h3>
          {/* Toggle pill */}
          <div className="flex bg-[#F3F4F6] rounded-full p-1 text-xs shrink-0 max-w-full overflow-x-auto no-scrollbar">
            <button
              type="button"
              onClick={() => {
                updateField({ isMultiDay: false });
                if (formData.startDate) {
                  updateField({ endDate: formData.startDate });
                } else {
                  updateField({ endDate: "" });
                }
              }}
              className={`px-3 py-1 rounded-full transition-colors font-semibold text-xs whitespace-nowrap ${!isMultiDay ? "bg-white shadow-sm text-[#FF4C18]" : "text-gray-400 font-normal"}`}
            >
              Single Day
            </button>
            <button
              type="button"
              onClick={() => updateField({ isMultiDay: true, endDate: "" })}
              className={`px-3 py-1 rounded-full transition-colors font-semibold text-xs whitespace-nowrap ${isMultiDay ? "bg-white shadow-sm text-[#FF4C18]" : "text-gray-400 font-normal"}`}
            >
              Multiple Dates
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
          <div className="flex flex-col gap-0.5 relative">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Start Date</label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCalendar((prev) => (prev === "start" ? false : "start"));
              }}
              className={`w-full border border-gray-200 rounded-lg h-[36px] px-3 outline-none text-left flex items-center justify-between gap-1 ${showCalendar === "start" ? "border-[#FF4C18]" : "hover:border-gray-300"} bg-white`}
            >
              <span className={`truncate flex-1 text-xs sm:text-sm ${formData.startDate ? "text-gray-900 font-medium" : "text-gray-400"}`}>
                {formData.startDate ? formData.startDate.split("-").reverse().join("-") : "26-01-2026"}
              </span>
              <CalendarIcon size={15} className="text-gray-400 flex-shrink-0" />
            </button>
            {showCalendar === "start" && (
              <CustomCalendar
                dateStart={dateStart}
                setDateStart={(d) => {
                  const iso = toLocalIsoDate(d);
                  if (isMultiDay) {
                    updateField({ startDate: iso, endDate: "" });
                  } else {
                    updateField({ startDate: iso, endDate: iso });
                  }
                }}
                dateEnd={null}
                setDateEnd={null}
                onClose={() => setShowCalendar(false)}
                minDate={minDateForStart}
                singleDateMode={true}
                overlay={true}
              />
            )}
          </div>

          {isMultiDay && (
            <div className="flex flex-col gap-0.5 relative">
              <label className="text-xs sm:text-sm font-normal text-gray-400">End Date</label>
              <button
                type="button"
                disabled={!formData.startDate}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCalendar((prev) => (prev === "end" ? false : "end"));
                }}
                className={`w-full border border-gray-200 rounded-lg h-[36px] px-3 outline-none text-left flex items-center justify-between gap-1 ${!formData.startDate ? "bg-gray-100 cursor-not-allowed opacity-60" : showCalendar === "end" ? "border-[#FF4C18]" : "hover:border-gray-300"} bg-white`}
              >
                <span className={`truncate flex-1 text-xs sm:text-sm ${formData.endDate ? "text-gray-900 font-medium" : "text-gray-400"}`}>
                  {formData.endDate ? formData.endDate.split("-").reverse().join("-") : "26-01-2026"}
                </span>
                <CalendarIcon size={15} className="text-gray-400 flex-shrink-0" />
              </button>
              {showCalendar === "end" && (
                <CustomCalendar
                  dateStart={dateEnd}
                  setDateStart={(d) => updateField({ endDate: toLocalIsoDate(d) })}
                  dateEnd={null}
                  setDateEnd={null}
                  onClose={() => setShowCalendar(false)}
                  minDate={dateStart || minDateForStart}
                  singleDateMode={true}
                  overlay={true}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Section 3: Role Selection */}
      {!isDirectQuote && !isEditMode && (
        <div className="mt-1">
          <h3 className="text-base sm:text-lg font-bold text-gray-950 mb-2">Who do you need?</h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-3.5 w-full items-stretch">

            {/* Event Planner */}
            <button
              type="button"
              onClick={() => updateField({ hiringRole: 'planner' })}
              className={`flex flex-col justify-between p-2.5 sm:p-3.5 rounded-xl border-[1.5px] text-left transition-all bg-white h-full min-h-[110px] sm:min-h-[120px] cursor-pointer ${
                formData.hiringRole === 'planner'
                  ? "border-[#FF4C18] shadow-[0_4px_16px_rgba(255,76,24,0.08)]"
                  : "border-transparent shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
              }`}
            >
              <div>
                <EventPlannerIcon className="w-5 h-5 sm:w-5.5 sm:h-5.5 mb-1 sm:mb-1.5 text-gray-900 shrink-0" />
                <h4 className="text-xs sm:text-sm font-bold text-gray-900 mb-0.5 sm:mb-1 leading-tight">
                  Event Planner
                </h4>
                <p className="text-[10px] sm:text-xs text-[#71717A] font-normal leading-tight sm:leading-relaxed">
                  Event planners, agencies and managers for end-to-end execution.
                </p>
              </div>
            </button>

            {/* Performer */}
            <button
              type="button"
              onClick={() => updateField({ hiringRole: 'performer' })}
              className={`flex flex-col justify-between p-2.5 sm:p-3.5 rounded-xl border-[1.5px] text-left transition-all bg-white h-full min-h-[110px] sm:min-h-[120px] cursor-pointer ${
                formData.hiringRole === 'performer'
                  ? "border-[#FF4C18] shadow-[0_4px_16px_rgba(255,76,24,0.08)]"
                  : "border-transparent shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
              }`}
            >
              <div>
                <Mic className="w-5 h-5 sm:w-5.5 sm:h-5.5 mb-1 sm:mb-1.5 text-gray-900 shrink-0 stroke-[1.8]" />
                <h4 className="text-xs sm:text-sm font-bold text-gray-900 mb-0.5 sm:mb-1 leading-tight">
                  Performer
                </h4>
                <p className="text-[10px] sm:text-xs text-[#71717A] font-normal leading-tight sm:leading-relaxed">
                  Artists and entertainers such as DJs, singers, anchors and MCs.
                </p>
              </div>
            </button>

            {/* Crew & Staff */}
            <button
              type="button"
              onClick={() => updateField({ hiringRole: 'crew' })}
              className={`flex flex-col justify-between p-2.5 sm:p-3.5 rounded-xl border-[1.5px] text-left transition-all bg-white h-full min-h-[110px] sm:min-h-[120px] cursor-pointer ${
                formData.hiringRole === 'crew'
                  ? "border-[#FF4C18] shadow-[0_4px_16px_rgba(255,76,24,0.08)]"
                  : "border-transparent shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
              }`}
            >
              <div>
                <HardHat className="w-5 h-5 sm:w-5.5 sm:h-5.5 mb-1 sm:mb-1.5 text-gray-900 shrink-0 stroke-[1.8]" />
                <h4 className="text-xs sm:text-sm font-bold text-gray-900 mb-0.5 sm:mb-1 leading-tight">
                  Crew & Staff
                </h4>
                <p className="text-[10px] sm:text-xs text-[#71717A] font-normal leading-tight sm:leading-relaxed">
                  On-ground support such as promoters, volunteers, hostesses and event crew.
                </p>
              </div>
            </button>

          </div>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end mt-auto pt-2 sm:pt-3">
        <button
          type="button"
          onClick={handleNext}
          className="bg-[#FF4C18] hover:bg-[#E63E0F] text-white px-6 py-2 rounded-lg font-medium text-xs sm:text-sm shadow-sm hover:shadow transition-all whitespace-nowrap cursor-pointer"
        >
          Continue &rarr;
        </button>
      </div>
    </div>
  );
}
